/**
 * Config Wizard — iter357
 *
 * Single-page setup health check for the CRM. Loads from
 * `/api/v1/integrations/wizard` and shows a progress card per integration:
 *
 *  • AI / LLM (Emergent universal or individual providers)
 *  • SMS (GoSMS OAuth2)
 *  • Microsoft 365
 *  • E-mail transport (Resend / SendGrid)
 *  • Stock photos & maps
 *
 * Each card links to the relevant integrations page when keys are missing.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, AlertTriangle, ExternalLink, Download, RefreshCw, Info } from "lucide-react";

const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const SEVERITY_BORDER = {
  error: "border-red-400",
  warning: "border-amber-300",
  info: "border-slate-200",
  ok: "border-emerald-200",
};
const SEVERITY_ICON = {
  error: <AlertTriangle className="w-5 h-5 text-red-600" />,
  warning: <AlertCircle className="w-5 h-5 text-amber-500" />,
  info: <Info className="w-5 h-5 text-slate-400" />,
  ok: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
};
const SEVERITY_BAR = {
  error: "bg-red-500",
  warning: "bg-amber-400",
  info: "bg-slate-300",
  ok: "bg-emerald-500",
};
const BADGE_STYLE = {
  "PRIMÁRNÍ": "bg-emerald-600 text-white",
  "ALTERNATIVA": "bg-blue-500 text-white",
  "ZÁLOHA": "bg-slate-400 text-white",
};

export default function ConfigWizard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reindexing, setReindexing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_V1}/integrations/wizard`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chyba při načítání");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const reindex = async () => {
    setReindexing(true);
    try {
      const r = await axios.post(`${API_V1}/search/reindex`);
      const ok = Object.values(r.data?.summary || {}).filter(v => v === "created" || v === "up_to_date").length;
      toast.success(`Reindex hotov — ${ok} kolekcí zaindexováno.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reindex selhal");
    }
    setReindexing(false);
  };

  const exportConfig = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API_V1}/integrations/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `solar-green-config-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Konfigurace stažena (bez tajných hodnot).");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export selhal");
    }
    setExporting(false);
  };

  if (loading) return <div className="p-8 text-slate-500">Načítám…</div>;
  if (!data) return null;

  const total = data.items.length;
  const complete = data.items.filter(it => it.status.complete).length;
  const required = data.items.filter(it => it.required);
  const requiredOk = required.filter(it => it.status.complete).length;
  const blocking = data.blocking_issues || [];

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-6" data-testid="config-wizard">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Konfigurační průvodce</h1>
          <p className="text-sm text-slate-500 mt-1">
            Kontrola stavu integrací a firemních údajů. Hotovo: <b>{complete} / {total}</b>,
            povinné: <b className={requiredOk === required.length ? "text-emerald-600" : "text-red-600"}>
              {requiredOk} / {required.length}
            </b>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load}
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5"
            data-testid="cw-refresh">
            <RefreshCw className="w-4 h-4" /> Obnovit
          </button>
          <button onClick={reindex} disabled={reindexing}
            className="px-3 py-2 rounded-lg bg-violet-100 text-violet-700 text-sm font-semibold hover:bg-violet-200 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="cw-reindex">
            <RefreshCw className={`w-4 h-4 ${reindexing ? "animate-spin" : ""}`} /> Reindex search
          </button>
          <button onClick={exportConfig} disabled={exporting}
            className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="cw-export">
            <Download className="w-4 h-4" /> Export config
          </button>
        </div>
      </div>

      {/* iter359 — Blocking issues banner */}
      {blocking.length > 0 && (
        <div className="rounded-xl border-2 border-red-400 bg-red-50 px-5 py-4" data-testid="cw-blocking-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-red-800">Vyžaduje pozornost ({blocking.length})</h3>
              <ul className="mt-2 space-y-1">
                {blocking.map((issue, i) => (
                  <li key={i} className="text-sm text-red-700 flex items-start gap-2">
                    <span className="text-red-400 mt-0.5">•</span> {issue}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Cards */}
      <div className="grid sm:grid-cols-2 gap-4">
        {data.items.map(item => {
          const sev = item.severity || (item.status.complete ? "ok" : item.required ? "error" : "info");
          const borderCls = SEVERITY_BORDER[sev] || "border-slate-200";
          const barCls = SEVERITY_BAR[sev] || "bg-slate-300";
          return (
          <div key={item.id}
            data-testid={`cw-card-${item.id}`}
            data-severity={sev}
            className={`rounded-xl border-2 p-5 bg-white ${borderCls}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {SEVERITY_ICON[sev]}
                  <h3 className="font-bold text-slate-800">{item.name}</h3>
                  {item.required && sev !== "ok" && (
                    <span className="text-[10px] uppercase tracking-wide bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">Povinné</span>
                  )}
                  {item.badge && (
                    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-bold ${BADGE_STYLE[item.badge] || "bg-slate-200 text-slate-700"}`}>
                      {item.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1.5">{item.description}</p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full transition-all ${barCls}`}
                style={{ width: `${item.status.progress}%` }} />
            </div>
            <div className="mt-2 text-[11px] text-slate-500">
              {item.status.present.length} / {item.status.present.length + item.status.missing.length} polí nastaveno
              {item.status.missing.length > 0 && (
                <span className={`ml-1.5 ${sev === "error" ? "text-red-600 font-semibold" : "text-amber-600"}`}>
                  · chybí: {item.status.missing.slice(0, 4).join(", ")}{item.status.missing.length > 4 ? "…" : ""}
                </span>
              )}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Link to="/admin/settings" className="text-xs font-semibold text-emerald-700 hover:underline"
                data-testid={`cw-${item.id}-configure`}>
                {item.status.complete ? "Upravit" : "Nastavit"} →
              </Link>
              {item.docs_url && (
                <a href={item.docs_url} target="_blank" rel="noreferrer"
                  className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> {item.docs_url.startsWith("/admin") ? "Otevřít v CRM" : "Získat klíč"}
                </a>
              )}
            </div>
          </div>
        );})}
      </div>

      <div className="text-[11px] text-slate-400 text-center pt-4">
        Tip: Po importu zálohy musíte tajné hodnoty (API klíče) zadat znovu — neukládáme je v plaintextu pro export.
      </div>
    </div>
  );
}
